// M17 P3 — Selection tab: candidates whose interview happened, awaiting the
// admin's decision. Recruiters see their own records read-only; ONLY admins
// can decide Joined / Not Interested / Reschedule. No auto-Employment — Joined
// reuses the frozen M04 employment creation rules.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, CalendarClock, CalendarDays, CheckCircle2, ClipboardCheck, Loader2, Phone, UserX,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { WhatsAppCallbackButton } from "@/components/common/WhatsAppCallbackButton";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const STATUS_BADGE = {
  "Status Pending": "bg-violet-50 text-violet-700 border-violet-200",
  "Awaiting Decision": "bg-violet-50 text-violet-700 border-violet-200",
  Selected: "bg-indigo-600 text-white border-indigo-600",
  Joined: "bg-emerald-600 text-white border-emerald-600",
  "Not Interested": "bg-slate-100 text-slate-600 border-slate-200",
  "Rejected by Company": "bg-red-50 text-red-700 border-red-200",
  "Candidate Not Interested": "bg-amber-50 text-amber-700 border-amber-200",
  Rescheduled: "bg-sky-50 text-sky-700 border-sky-200",
};

const CLOSED_STATUSES = ["Not Interested", "Rejected by Company", "Candidate Not Interested"];
const PENDING_STATUSES = ["Status Pending", "Awaiting Decision"];

export default function SelectionList() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const appBase = isAdmin ? "/admin/applications" : "/recruiter/applications";

  const [tab, setTab] = useState("Status Pending");
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // admin decision dialogs
  const [joinFor, setJoinFor] = useState(null);
  const [joinForm, setJoinForm] = useState({ date_of_joining: "", locking_template: "", remarks: "", payout: "", profile: "", process_name: "", location: "", lead_specialist: "" });
  const [lockingTemplates, setLockingTemplates] = useState([]);
  const [niFor, setNiFor] = useState(null);
  const [niNote, setNiNote] = useState("");
  const [niType, setNiType] = useState("");
  const [reschedFor, setReschedFor] = useState(null);
  const [reschedForm, setReschedForm] = useState({ company_id: "", interview_date: "", role: "" });
  const [companies, setCompanies] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/recruiter-flow/selection", { params: { status: tab } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/master-data").then((r) => {
      const md = r.data;
      const vals = (md.locking_period_template || []).map((v) => (typeof v === "string" ? v : v.value));
      setLockingTemplates(vals);
    }).catch(() => {});
    api.get("/companies", { params: { limit: 200 } })
      .then((r) => setCompanies(r.data.items || r.data || [])).catch(() => {});
  }, [isAdmin]);

  const openJoin = (s) => {
    setJoinFor(s);
    setJoinForm({ date_of_joining: "", locking_template: "", remarks: "", payout: "",
                  profile: s.role || "", process_name: "", location: "", lead_specialist: "" });
    api.get(`/recruiter-flow/selection/${s.id}/join-prefill`)
      .then((r) => setJoinForm((f) => ({
        ...f,
        profile: r.data.profile || f.profile,
        location: r.data.location || "",
        lead_specialist: r.data.lead_specialist || "",
      })))
      .catch(() => {});
  };

  const submitJoined = async () => {
    if (!joinForm.date_of_joining) return toast.error("Pick the date of joining");
    if (!joinForm.locking_template) return toast.error("Choose the locking period template");
    setBusy(true);
    try {
      const res = await api.post(`/recruiter-flow/selection/${joinFor.id}/joined`, {
        date_of_joining: joinForm.date_of_joining,
        locking_template: joinForm.locking_template,
        remarks: joinForm.remarks || undefined,
        payout: joinForm.payout !== "" ? Number(joinForm.payout) : undefined,
        profile: joinForm.profile || undefined,
        process_name: joinForm.process_name || undefined,
        location: joinForm.location || undefined,
        lead_specialist: joinForm.lead_specialist || undefined,
      });
      toast.success(`${joinFor.candidate_name} joined — employment ${res.data.employment?.emp_code} created`);
      setJoinFor(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNotInterested = async () => {
    if (!niType) return toast.error("Choose the closure reason — Rejected by Company or Candidate Not Interested");
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/selection/${niFor.id}/not-interested`, {
        closure_type: niType, note: niNote || undefined,
      });
      toast.success(`${niFor.candidate_name} closed (${niType}) — candidate is back in the Candidate Directory`);
      setNiFor(null);
      setNiNote("");
      setNiType("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReschedule = async () => {
    if (!reschedForm.company_id) return toast.error("Choose the company");
    if (!reschedForm.interview_date) return toast.error("Pick the new interview date");
    if ((reschedForm.role || "").trim().length < 2) return toast.error("Enter the role / profile");
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/selection/${reschedFor.id}/reschedule`, reschedForm);
      toast.success(`${reschedFor.candidate_name} rescheduled — back in the recruiter's Scheduled tab`);
      setReschedFor(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const markSelected = async (s) => {
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/selection/${s.id}/selected`);
      toast.success(`${s.candidate_name} marked Selected — now in the Selection tab, ready to Mark Joined`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Selection</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isAdmin
            ? "Interviews that took place. In Status Pending decide Selected, Rejected by Company / Candidate Not Interested, or Reschedule. In Selection, Mark Joined (or close if the candidate backs out)."
            : "Candidates whose interview happened. The admin records the outcome — you will see it here and on your dashboard."}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="Status Pending" data-testid="sel-tab-status-pending">Status Pending</TabsTrigger>
          <TabsTrigger value="Selected" data-testid="sel-tab-selection">Selection</TabsTrigger>
          <TabsTrigger value="Joined" data-testid="sel-tab-joined">Joined</TabsTrigger>
          <TabsTrigger value="Not Interested" data-testid="sel-tab-not-interested">Rejected / Not Interested</TabsTrigger>
          <TabsTrigger value="Rescheduled" data-testid="sel-tab-rescheduled">Rescheduled</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : data.items.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={ClipboardCheck} title={`No records in ${tab === "Selected" ? "Selection" : tab}`}
            description={PENDING_STATUSES.includes(tab)
              ? "When an interview happens on the Going board, the candidate appears here."
              : tab === "Selected"
                ? "Mark a candidate Selected from Status Pending and they'll appear here, ready to Mark Joined."
                : "Nothing here yet."}
            testId="selection-empty" />
        </Card>
      ) : (
        <div className="space-y-2.5" data-testid="selection-list">
          {data.items.map((s) => (
            <Card key={s.id} className="rounded-xl border-border shadow-none" data-testid="selection-item">
              <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-tight">
                    {s.candidate_name}
                    <span className="font-mono text-[11px] text-muted-foreground ml-2">{s.sel_code}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{s.company_name}</span>
                    <span>· {s.role || "—"}</span>
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />Interviewed {formatDate(s.interview_date)}</span>
                    {s.candidate_mobile && (
                      <a href={`tel:${s.candidate_mobile}`} className="inline-flex items-center gap-1 hover:text-foreground">
                        <Phone className="h-3 w-3" /><span className="font-mono">{s.candidate_mobile}</span>
                      </a>
                    )}
                    <Link to={`${appBase}/${s.application_id}`} className="font-mono text-primary hover:underline" data-testid="selection-app-link">
                      {s.app_code}
                    </Link>
                    {isAdmin && s.recruiter_name && <span>· Recruiter: {s.recruiter_name}</span>}
                  </p>
                  {s.outcome && (
                    <p className="text-[11px] text-muted-foreground mt-1" data-testid="selection-outcome">
                      {s.status === "Joined" && `Joined ${formatDate(s.outcome.date_of_joining)} — employment ${s.outcome.emp_code}`}
                      {s.status === "Rescheduled" && `Rescheduled — ${s.outcome.resched_code}`}
                      {CLOSED_STATUSES.includes(s.status) && (s.outcome.note || "Closed by admin")}
                      {s.outcome.decided_by && ` · by ${s.outcome.decided_by}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <Badge variant="outline" className={`rounded-full ${STATUS_BADGE[s.status] || ""}`} data-testid="selection-status-badge">
                    {s.status}
                  </Badge>
                  {[...PENDING_STATUSES, "Selected"].includes(s.status) && (
                    <WhatsAppCallbackButton candidateName={s.candidate_name} candidateMobile={s.candidate_mobile} />
                  )}
                  {isAdmin && PENDING_STATUSES.includes(s.status) && (
                    <>
                      <Button size="sm" className="rounded-lg gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => markSelected(s)} disabled={busy}
                              data-testid={`sel-selected-${s.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Selected
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg gap-1.5 bg-white"
                              onClick={() => { setReschedFor(s); setReschedForm({ company_id: "", interview_date: "", role: s.role || "" }); }}
                              data-testid={`sel-reschedule-${s.id}`}>
                        <CalendarClock className="h-3.5 w-3.5" /> Reschedule
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg gap-1.5 bg-white text-red-600 hover:text-red-700"
                              onClick={() => { setNiFor(s); setNiNote(""); setNiType(""); }}
                              data-testid={`sel-not-interested-${s.id}`}>
                        <UserX className="h-3.5 w-3.5" /> Reject / Not Interested
                      </Button>
                    </>
                  )}
                  {isAdmin && s.status === "Selected" && (
                    <>
                      <Button size="sm" className="rounded-lg gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => openJoin(s)}
                              data-testid={`sel-joined-${s.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark Joined
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg gap-1.5 bg-white text-red-600 hover:text-red-700"
                              onClick={() => { setNiFor(s); setNiNote(""); setNiType(""); }}
                              data-testid={`sel-not-interested-${s.id}`}>
                        <UserX className="h-3.5 w-3.5" /> Not Interested / Rejected
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Joined dialog (admin) */}
      <Dialog open={!!joinFor} onOpenChange={(o) => !o && setJoinFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="sel-joined-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm joining — {joinFor?.candidate_name}</DialogTitle>
            <DialogDescription>
              Creates the employment record for {joinFor?.company_name} with the standard locking rules.
              It appears under Joining Confirmation immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Date of joining<span className="text-destructive ml-0.5">*</span></Label>
              <Input type="date" data-testid="sel-joining-date" value={joinForm.date_of_joining}
                     onChange={(e) => setJoinForm({ ...joinForm, date_of_joining: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Locking period<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={joinForm.locking_template} onValueChange={(v) => setJoinForm({ ...joinForm, locking_template: v })}>
                <SelectTrigger data-testid="sel-locking-select"><SelectValue placeholder="Choose template..." /></SelectTrigger>
                <SelectContent>
                  {lockingTemplates.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Profile / role-seniority</Label>
              <Input data-testid="sel-joined-profile" value={joinForm.profile}
                     placeholder="e.g. Senior Sales Executive"
                     onChange={(e) => setJoinForm({ ...joinForm, profile: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Process name</Label>
              <Input data-testid="sel-joined-process" value={joinForm.process_name}
                     placeholder="e.g. Inbound Voice Process"
                     onChange={(e) => setJoinForm({ ...joinForm, process_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input data-testid="sel-joined-location" value={joinForm.location}
                     placeholder="e.g. Gurugram"
                     onChange={(e) => setJoinForm({ ...joinForm, location: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Payout <span className="text-xs text-muted-foreground">(admin-only)</span></Label>
              <Input type="number" min="0" step="any" data-testid="sel-joined-payout" value={joinForm.payout}
                     placeholder="Amount"
                     onChange={(e) => setJoinForm({ ...joinForm, payout: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Lead Specialist</Label>
              <Input data-testid="sel-joined-lead-specialist" value={joinForm.lead_specialist}
                     placeholder="Auto-filled from forward history, editable"
                     onChange={(e) => setJoinForm({ ...joinForm, lead_specialist: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Remarks (optional)</Label>
              <Textarea rows={2} data-testid="sel-joined-remarks" value={joinForm.remarks}
                        onChange={(e) => setJoinForm({ ...joinForm, remarks: e.target.value })} />
            </div>
            <Button onClick={submitJoined} disabled={busy} className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sel-joined-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm Joined
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close after interview dialog (admin) — Rejected by Company / Candidate Not Interested */}
      <Dialog open={!!niFor} onOpenChange={(o) => !o && setNiFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="sel-ni-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Close after interview — {niFor?.candidate_name}</DialogTitle>
            <DialogDescription>
              The application is closed and the candidate becomes available again in the
              Candidate Directory. The reason you pick here is recorded for reporting. Full history is kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Reason<span className="text-destructive ml-0.5">*</span></Label>
              <RadioGroup value={niType} onValueChange={setNiType} className="gap-2">
                <label htmlFor="ni-rejected-company"
                       className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${niType === "Rejected by Company" ? "border-red-300 bg-red-50/60" : "border-border hover:bg-muted/50"}`}>
                  <RadioGroupItem value="Rejected by Company" id="ni-rejected-company" className="mt-0.5"
                                  data-testid="sel-ni-type-company" />
                  <span>
                    <span className="text-sm font-medium block">Rejected by Company</span>
                    <span className="text-xs text-muted-foreground">The company did not select the candidate after the interview.</span>
                  </span>
                </label>
                <label htmlFor="ni-candidate-ni"
                       className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${niType === "Candidate Not Interested" ? "border-amber-300 bg-amber-50/60" : "border-border hover:bg-muted/50"}`}>
                  <RadioGroupItem value="Candidate Not Interested" id="ni-candidate-ni" className="mt-0.5"
                                  data-testid="sel-ni-type-candidate" />
                  <span>
                    <span className="text-sm font-medium block">Candidate Not Interested</span>
                    <span className="text-xs text-muted-foreground">The candidate declined the offer or lost interest.</span>
                  </span>
                </label>
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea rows={2} data-testid="sel-ni-note" value={niNote} onChange={(e) => setNiNote(e.target.value)}
                        placeholder="e.g. Rejected in the final round / candidate accepted another offer" />
            </div>
            <Button onClick={submitNotInterested} disabled={busy || !niType} variant="destructive" className="w-full rounded-lg" data-testid="sel-ni-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm closure
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog (admin) */}
      <Dialog open={!!reschedFor} onOpenChange={(o) => !o && setReschedFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="sel-resched-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reschedule — {reschedFor?.candidate_name}</DialogTitle>
            <DialogDescription>
              Sends the candidate back to {reschedFor?.recruiter_name || "the recruiter"}'s Scheduled tab with a
              new company and date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Company<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={reschedForm.company_id} onValueChange={(v) => setReschedForm({ ...reschedForm, company_id: v })}>
                <SelectTrigger data-testid="sel-resched-company"><SelectValue placeholder="Choose company..." /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>New interview date<span className="text-destructive ml-0.5">*</span></Label>
              <Input type="date" data-testid="sel-resched-date" value={reschedForm.interview_date}
                     min={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setReschedForm({ ...reschedForm, interview_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Role / profile<span className="text-destructive ml-0.5">*</span></Label>
              <Input data-testid="sel-resched-role" value={reschedForm.role}
                     onChange={(e) => setReschedForm({ ...reschedForm, role: e.target.value })} />
            </div>
            <Button onClick={submitReschedule} disabled={busy} className="w-full rounded-lg" data-testid="sel-resched-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Reschedule interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
